/**
 * TrackingDashboardWidget — Fase A Afronding (22 Feb 2026).
 *
 * Uitgebreide tracking-widget voor het dashboard. Toont:
 *   1. Recente Tracking Events (laatste 5-10 events, klikbaar → zending)
 *   2. Canonical statusverdeling over laatste 7 dagen (horizontale bar-chart)
 *
 * Gebruikt bestaande endpoints:
 *   - GET /api/admin/tracking-updater/stats     — breakdown_last_7d + canonical_statuses
 *   - GET /api/admin/tracking-updater/events    — recente events
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Package, Container, Truck, ArrowRight, Radar, Loader2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { AdminSkeleton } from "../ui";

const ENTITY_ICONS = {
  consolidation: Container,
  shipment:      Package,
  booking:       Truck,
};

const STATUS_LABEL = {
  consolidatie_aangemaakt: "Consolidatie aangemaakt",
  consolidatie_gesloten:   "Consolidatie gesloten",
  in_warehouse_ontvangen:  "In warehouse",
  container_samengesteld:  "Container samengesteld",
  vertrokken:              "Vertrokken",
  onderweg:                "Onderweg",
  aangekomen:              "Aangekomen",
  douane:                  "Douane",
  vrijgegeven:             "Vrijgegeven",
  klaar_voor_afhalen:      "Klaar voor afhalen",
  delivery_ingepland:      "Delivery ingepland",
  afgeleverd:              "Afgeleverd",
  afgesloten:              "Afgesloten",
};

const STATUS_TONE = {
  consolidatie_aangemaakt: "bg-slate-200",
  consolidatie_gesloten:   "bg-slate-400",
  in_warehouse_ontvangen:  "bg-blue-400",
  container_samengesteld:  "bg-blue-500",
  vertrokken:              "bg-indigo-500",
  onderweg:                "bg-indigo-600",
  aangekomen:              "bg-purple-500",
  douane:                  "bg-amber-500",
  vrijgegeven:             "bg-emerald-400",
  klaar_voor_afhalen:      "bg-emerald-500",
  delivery_ingepland:      "bg-emerald-600",
  afgeleverd:              "bg-emerald-700",
  afgesloten:              "bg-slate-500",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("nl-NL", {
      day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function TrackingDashboardWidget({ onOpenShipment }) {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, eRes] = await Promise.all([
        adminApi.get("/admin/tracking-updater/stats"),
        adminApi.get("/admin/tracking-updater/events?limit=8"),
      ]);
      setStats(sRes.data);
      setEvents(eRes.data?.events || []);
    } catch (e) {
      toast.error(formatApiError(e, "Tracking-data laden mislukt"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const handleClickEvent = (ev) => {
    if (!onOpenShipment) return;
    if (ev.entity_type === "shipment") {
      onOpenShipment(ev);
    } else if (ev.entity_type === "consolidation") {
      window.location.assign(`/admin?section=consolidations&consolidation_id=${ev.entity_id}`);
    }
  };

  const breakdown = stats?.breakdown_last_7d || [];
  const maxCount = Math.max(1, ...breakdown.map((b) => b.count));

  return (
    <div className="space-y-4" data-testid="tracking-dashboard-widget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Radar className="w-4 h-4 text-blue-600" />
          <div className="text-sm font-semibold text-slate-800">Tracking activiteit</div>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-50"
          data-testid="tracking-dashboard-refresh"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Ververs
        </button>
      </div>

      {loading ? (
        <AdminSkeleton variant="lines" rows={5} />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recente events */}
          <div className="space-y-2" data-testid="tracking-recent-events">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Recente events ({events.length})
            </div>
            {events.length === 0 ? (
              <div className="text-xs text-slate-500 italic border border-dashed border-slate-300 rounded-lg p-4 text-center">
                Nog geen tracking-events geregistreerd.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {events.slice(0, 8).map((ev) => {
                  const Icon = ENTITY_ICONS[ev.entity_type] || Package;
                  const statusLabel = STATUS_LABEL[ev.new_status] || ev.new_status || "?";
                  const tone = STATUS_TONE[ev.new_status] || "bg-slate-300";
                  return (
                    <li
                      key={ev.id}
                      className="flex items-center gap-2.5 text-sm px-2.5 py-2 border border-slate-100 rounded-lg hover:bg-brand-50/40 hover:border-brand-200 transition-colors cursor-pointer"
                      onClick={() => handleClickEvent(ev)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClickEvent(ev); }}
                      role="button"
                      tabIndex={0}
                      data-testid={`tracking-event-row-${ev.id}`}
                    >
                      <div className="w-7 h-7 rounded-md bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${tone}`} />
                          <span className="text-xs font-semibold text-brand-900 truncate">{statusLabel}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                          <span className="font-mono truncate">{ev.entity_label || ev.entity_id?.slice(0, 8)}</span>
                          <span>·</span>
                          <span className="capitalize">{ev.entity_type}</span>
                        </div>
                      </div>
                      <span className="text-[11px] text-slate-400 tabular-nums flex-shrink-0">
                        {fmtDate(ev.created_at)}
                      </span>
                      <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Statusverdeling — horizontale bar chart */}
          <div className="space-y-2" data-testid="tracking-status-breakdown">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              Statusverdeling (7 dagen)
            </div>
            {breakdown.length === 0 ? (
              <div className="text-xs text-slate-500 italic border border-dashed border-slate-300 rounded-lg p-4 text-center">
                Geen data voor laatste 7 dagen.
              </div>
            ) : (
              <ul className="space-y-1.5">
                {breakdown.slice(0, 10).map((b) => {
                  const label = STATUS_LABEL[b.status] || (b.status || "onbekend").replace(/_/g, " ");
                  const tone = STATUS_TONE[b.status] || "bg-slate-300";
                  const pct = Math.round((b.count / maxCount) * 100);
                  return (
                    <li key={b.status} className="text-xs" data-testid={`tracking-status-bar-${b.status}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-slate-700 font-medium truncate">{label}</span>
                        <span className="text-slate-500 tabular-nums font-mono">{b.count}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${tone} rounded-full transition-all duration-500`}
                          style={{ width: `${Math.max(2, pct)}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
